import moment from "moment";

export const DEFAULT_VAT_PERCENTAGE = 15;
export const VAT_RATE = DEFAULT_VAT_PERCENTAGE / 100;
export const PREVIEW_KEY = "fleetos_quote_preview";

export const resolveVatPercentage = (source) => {
  const raw =
    source?.vatPercentage ??
    source?.attributes?.vatPercentage ??
    source?.vatPercent ??
    (typeof source === "number" || typeof source === "string" ? source : null);
  if (raw === null || raw === undefined || raw === "") {
    return DEFAULT_VAT_PERCENTAGE;
  }
  const num = Number(raw);
  if (!Number.isFinite(num) || num < 0) return DEFAULT_VAT_PERCENTAGE;
  // support both 15 and 0.15 styles
  if (num > 0 && num <= 1) return +(num * 100).toFixed(4);
  return num;
};

export const resolveVatRate = (source) => resolveVatPercentage(source) / 100;

export const FUEL_TYPE_OPTIONS = [
  { name: "PETROL", key: "PETROL" },
  { name: "DIESEL", key: "DIESEL" },
  { name: "ETHANOL", key: "ETHANOL" },
  { name: "BIODIESEL", key: "BIODIESEL" },
  { name: "CNG", key: "CNG" },
  { name: "LPG", key: "LPG" },
  { name: "HYDROGEN", key: "HYDROGEN" },
  { name: "HYBRID", key: "HYBRID" },
  { name: "PHEV", key: "PHEV" },
  { name: "ELECTRIC", key: "ELECTRIC" },
  { name: "OTHERS", key: "OTHERS" },
];

export const FALLBACK_VEHICLE_TYPES = [
  "Sedan",
  "SUV",
  "Van",
  "Pickup",
  "Bus",
  "Truck",
  "Coupe",
  "Hatchback",
];

const DEFAULT_TERMS_EN =
  "This quotation is valid until the expiry date shown. Prices are subject to vehicle inspection and parts availability. This document is a quotation only and has no accounting or financial impact.";
const DEFAULT_TERMS_AR =
  "يسري عرض السعر حتى تاريخ الانتهاء الموضح. تخضع الأسعار للفحص الفني وتوفر القطع. هذه الوثيقة عرض سعر فقط وليس لها أثر محاسبي أو مالي.";

const VIN_BODY_TYPE_ALIASES = {
  "sedan/saloon": "Sedan",
  saloon: "Sedan",
  "station wagon": "Wagon",
  wagon: "Wagon",
  "sport utility vehicle": "SUV",
  suv: "SUV",
  "multi-purpose vehicle": "MPV",
  mpv: "MPV",
  pickup: "Pickup",
  truck: "Truck",
  hatchback: "Hatchback",
  coupe: "Coupe",
  convertible: "Convertible",
  van: "Van",
};

export const defaultTerms = () => ({
  termsEnglish: DEFAULT_TERMS_EN,
  termsArabic: DEFAULT_TERMS_AR,
});

export const emptyLineItem = () => ({
  serviceType: "",
  expenseType: null,
  expenseTypeId: null,
  expenseSubTypeId: null,
  item: null,
  itemId: null,
  itemName: "",
  itemCode: "",
  typeLabel: "",
  subTypeLabel: "",
  subTypeNum: "",
  description: "",
  quantity: 1,
  unitPrice: 0,
  cost: 0,
  withVat: true,
  vatAmount: 0,
  lineTotal: 0,
});

export const calcLine = (row, vatRate = VAT_RATE) => {
  const quantity = Number(row.quantity) || 0;
  const unitPrice = Number(row.unitPrice) || 0;
  const base = quantity * unitPrice;
  const rate = Number.isFinite(Number(vatRate)) ? Number(vatRate) : VAT_RATE;
  const vatAmount = row.withVat ? +(base * rate).toFixed(2) : 0;
  const lineTotal = +(base + vatAmount).toFixed(2);
  return { ...row, quantity, unitPrice, vatAmount, lineTotal };
};

export const calcTotals = (items = []) => {
  const subtotal = items.reduce((sum, row) => {
    const base = (Number(row.quantity) || 0) * (Number(row.unitPrice) || 0);
    return sum + base;
  }, 0);
  const vatTotal = items.reduce(
    (sum, row) => sum + (Number(row.vatAmount) || 0),
    0,
  );
  return {
    subtotal: +subtotal.toFixed(2),
    vatTotal: +vatTotal.toFixed(2),
    grandTotal: +(subtotal + vatTotal).toFixed(2),
  };
};

export const QUOTE_STATUS = {
  DRAFT: "Draft",
  APPROVED: "Approved",
  REJECTED: "Rejected",
  EXPIRED: "expired",
};

export const QUOTE_STATUS_FILTER_OPTIONS = [
  { id: "", labelKey: "all" },
  { id: QUOTE_STATUS.DRAFT, labelKey: "draft" },
  { id: QUOTE_STATUS.APPROVED, labelKey: "approved" },
  { id: QUOTE_STATUS.REJECTED, labelKey: "rejected" },
];

export const normalizeStatusValue = (status) => {
  if (!status) return QUOTE_STATUS.DRAFT;
  const value = String(status).trim().toLowerCase();
  if (value === "approved") return QUOTE_STATUS.APPROVED;
  if (value === "rejected") return QUOTE_STATUS.REJECTED;
  if (value === "expired") return QUOTE_STATUS.EXPIRED;
  if (value === "draft") return QUOTE_STATUS.DRAFT;
  return status;
};

export const resolveQuoteStatus = (quote) => {
  if (!quote) return QUOTE_STATUS.DRAFT;
  const rawStatus = normalizeStatusValue(quote.status);
  if (rawStatus === QUOTE_STATUS.APPROVED || rawStatus === QUOTE_STATUS.REJECTED) {
    return rawStatus;
  }
  if (
    quote.expiryDate &&
    moment(quote.expiryDate).endOf("day").isBefore(moment())
  ) {
    return QUOTE_STATUS.EXPIRED;
  }
  return rawStatus || QUOTE_STATUS.DRAFT;
};

/** Quote already converted to manual invoice once (attributes.mannualInvoice). */
export const isQuoteManualInvoiceCreated = (quote) => {
  if (!quote) return false;
  const attrs = quote.attributes || {};
  const flag =
    attrs.mannualInvoice ??
    attrs.manualInvoice ??
    quote.mannualInvoice ??
    quote.manualInvoice;
  return flag === true || flag === "true" || flag === 1 || flag === "1";
};

export const getQuoteStatusActor = (quote) => {
  const attrs = quote?.attributes || {};
  const status = resolveQuoteStatus(quote);
  if (status === QUOTE_STATUS.APPROVED) {
    return attrs.approvedBy || quote?.approvedBy || "";
  }
  if (status === QUOTE_STATUS.REJECTED) {
    return attrs.rejectedBy || quote?.rejectedBy || "";
  }
  return "";
};

export const normalizeQuote = (quote) => {
  if (!quote) return quote;
  const raw = quote.quote && typeof quote.quote === "object" ? quote.quote : quote;
  const rawItems =
    quote.quoteItemCollection ||
    quote.items ||
    quote.quoteItems ||
    raw.quoteItemCollection ||
    raw.items ||
    raw.quoteItems ||
    [];
  const vatPercentage = resolveVatPercentage(
    quote.vatPercentage ??
      raw.vatPercentage ??
      quote.attributes?.vatPercentage ??
      raw.attributes?.vatPercentage ??
      quote.account ??
      raw.account,
  );
  const vatRate = vatPercentage / 100;
  const items = (rawItems || []).map((item) => {
    const expenseTypeId = item.expenseTypeId || null;
    const expenseSubTypeId = item.expenseSubTypeId || null;
    const typeLabel =
      item.typeLabel || item.expenseType || item.serviceType || item.category || "";
    const subTypeLabel =
      item.subTypeLabel || item.itemName || item.label || "";
    return calcLine(
      {
        serviceType: item.serviceType || item.expenseType || item.category || typeLabel || "",
        expenseType: expenseTypeId
          ? {
              id: expenseTypeId,
              name: item.expenseType || typeLabel,
              label: typeLabel || item.expenseType || "",
            }
          : item.expenseType || item.serviceType || "",
        expenseTypeId,
        expenseSubTypeId,
        item: expenseSubTypeId
          ? {
              id: expenseSubTypeId,
              label: subTypeLabel,
              name: subTypeLabel,
              subTypeNum: item.subTypeNum || item.itemCode || "",
            }
          : item.item || null,
        itemId: item.itemId || expenseSubTypeId || item.id,
        itemName: subTypeLabel,
        itemCode: item.itemCode || item.subTypeNum || "",
        typeLabel,
        subTypeLabel,
        subTypeNum: item.subTypeNum || item.itemCode || "",
        description:
          item.description ||
          item.subTypeLabel|| 
          "",
        quantity: item.quantity || 1,
        unitPrice: item.unitPrice || item.saleCost || item.price || 0,
        cost: item.cost || 0,
        withVat:
          typeof item.withVat === "boolean"
            ? item.withVat
            : Number(item.saleVatAmount || item.vatAmount || item.vat || 0) > 0,
        vatAmount: item.saleVatAmount || item.vatAmount || item.vat || 0,
        lineTotal:
          item.saleNetAmount || item.lineTotal || item.totalSingleItemCost || 0,
      },
      vatRate,
    );
  });
  const totals = calcTotals(items);
  return {
    ...raw,
    ...quote,
    items,
    vatPercentage,
    quoteNumber:
      raw.quoteNumber ||
      quote.quoteNumber,
    quoteRefNo:
      raw.quoteRefNo ||
      quote.quoteRefNo ||
      quote.quoteNumber ||
      "",
    quoteDate:
      raw.quoteDate ||
      quote.quoteDate ||
      raw.quoteRefDate ||
      quote.createdAt ||
      quote.created ||
      quote.refDate,
    quoteRefDate: raw.quoteRefDate || quote.quoteRefDate,
    createdAt: raw.created || quote.created || quote.createdAt,
    expiryDate: raw.expiryDate || quote.expiryDate || quote.quotationExpiry,
    accountName:
      raw.accountName ||
      quote.accountName ||
      quote.account?.name ||
      quote.account?.label ||
      "",
    accountId:
      raw.accountId || quote.accountId || quote.account?.id || null,
    contractNumber:
      raw.contractNumber ||
      quote.contractNumber ||
      quote.account?.contractNumber ||
      "",
    account:
      quote.account ||
      raw.account ||
      (raw.accountId || quote.accountId
        ? {
            id: raw.accountId || quote.accountId,
            name:
              raw.accountName ||
              quote.accountName ||
              quote.account?.name ||
              "",
            label:
              raw.accountName ||
              quote.accountName ||
              quote.account?.label ||
              "",
            contractNumber:
              raw.contractNumber ||
              quote.contractNumber ||
              quote.account?.contractNumber ||
              "",
          }
        : null),
    contactPerson:
      raw.customerName || quote.customerName || quote.contactPerson || "",
    customerName:
      raw.customerName || quote.customerName || quote.contactPerson || "",
    mobile:
      raw.contactPersonMobile ||
      quote.contactPersonMobile ||
      quote.mobile ||
      "",
    contactPersonMobile:
      raw.contactPersonMobile ||
      quote.contactPersonMobile ||
      quote.mobile ||
      "",
    email: raw.email || quote.email || "",
    address: raw.address || quote.address || "",
    vatNumber: raw.vatNumber || quote.vatNumber || "",
    plate:
      raw.attributes?.licensePlate ||
      quote.attributes?.licensePlate ||
      quote.plate ||
      quote.vehicleLicensePlate ||
      raw.plate ||
      "",
    salesPersonId: raw.salePersonId || quote.salePersonId || quote.salesPersonId,
    salePersonId: raw.salePersonId || quote.salePersonId || quote.salesPersonId,
    salesPersonName:
      raw.attributes?.salePersonName ||
      quote.attributes?.salePersonName ||
      quote.salesPersonName ||
      quote.salesManagerName ||
      "",
    salesPersonEmail:
      raw.attributes?.salePersonEmail ||
      quote.attributes?.salePersonEmail ||
      quote.salesPersonEmail ||
      "",
    salesPersonMobile:
      raw.attributes?.salePersonPhone ||
      quote.attributes?.salePersonPhone ||
      raw.attributes?.salePersonMobile ||
      quote.attributes?.salePersonMobile ||
      quote.salesPersonMobile ||
      "",
    approvedBy:
      raw.attributes?.approvedBy ||
      quote.attributes?.approvedBy ||
      quote.approvedBy ||
      "",
    rejectedBy:
      raw.attributes?.rejectedBy ||
      quote.attributes?.rejectedBy ||
      quote.rejectedBy ||
      "",
    approvedAt:
      raw.attributes?.approvedAt ||
      quote.attributes?.approvedAt ||
      quote.approvedAt ||
      "",
    rejectedAt:
      raw.attributes?.rejectedAt ||
      quote.attributes?.rejectedAt ||
      quote.rejectedAt ||
      "",
    attributes: {
      ...(raw.attributes || {}),
      ...(quote.attributes || {}),
    },
    vehicleId: raw.vehicleId || quote.vehicleId || null,
    status: resolveQuoteStatus({ ...raw, ...quote, items }),
    subtotal: totals.subtotal,
    vatTotal: totals.vatTotal,
    grandTotal: totals.grandTotal,
    termsEnglish: quote.termsEnglish || raw.termsEnglish || DEFAULT_TERMS_EN,
    termsArabic: quote.termsArabic || raw.termsArabic || DEFAULT_TERMS_AR,
  };
};

export const buildQuoteRecord = (form, extras = {}) => {
  const vatPercentage = resolveVatPercentage(
    form.vatPercentage ?? form.account,
  );
  const vatRate = vatPercentage / 100;
  const items = (form.items || []).map((row) => {
    const line = calcLine(row, vatRate);
    const quantity = Number(line.quantity) || 0;
    const unitPrice = Number(line.unitPrice) || 0;
    return {
      ...line,
      cost: quantity * unitPrice,
    };
  });
  const totals = calcTotals(items);
  const quoteDate = extras.quoteDate || moment().toISOString();
  return {
    id: extras.id,
    quoteNumber: form.quoteRefNo || extras.quoteNumber || extras.quoteRefNo || "",
    quoteRefNo: form.quoteRefNo || extras.quoteRefNo || extras.quoteNumber || "",
    quoteDate,
    quoteRefDate: extras.quoteRefDate || quoteDate,
    currentDate: extras.currentDate || moment().toISOString(),
    createdAt: extras.createdAt || moment().toISOString(),
    expiryDate: form.expiryDate,
    status: extras.status || "Draft",
    accountId: form.account?.id || form.accountId || null,
    accountName:
      form.account?.name || form.account?.label || form.accountName || "",
    contractNumber:
      form.account?.contractNumber ||
      form.contractNumber ||
      extras.contractNumber ||
      "",
    contactPerson: form.contactPerson || form.customerName || "",
    customerName: form.contactPerson || form.customerName || "",
    mobile: form.mobile || form.contactPersonMobile || "",
    contactPersonMobile: form.mobile || form.contactPersonMobile || "",
    email: form.email || "",
    address: form.address || "",
    vatNumber: form.vatNumber || "",
    vin: form.vin || "",
    plate: form.plate || "",
    make: form.make?.label || form.make?.key || form.make || "",
    model: form.modelId?.modelName || form.modelId?.label || form.model || "",
    modelId: form.modelId?.id || form.modelId || null,
    year: form.year || "",
    fuelType: form.fuelType?.id || form.fuelType || "",
    vehicleType: form.vehicleType || "",
    vehicleId: form.vehicleId || extras.vehicleId || null,
    salesPersonId: form.salesPerson?.id || form.salesPersonId || form.salePersonId || null,
    salePersonId: form.salesPerson?.id || form.salesPersonId || form.salePersonId || null,
    salesPersonName:
      form.salesPerson?.name ||
      form.salesPerson?.label ||
      form.salesPersonName ||
      "",
    salesPersonEmail: form.salesPersonEmail || form.salesPerson?.email || "",
    salesPersonMobile:
      form.salesPersonMobile ||
      form.salesPerson?.phone ||
      form.salesPerson?.mobile ||
      "",
    createdById: extras.createdById || null,
    createdByName: extras.createdByName || "",
    companyId: extras.companyId || null,
    items,
    ...totals,
    termsEnglish: form.termsEnglish || DEFAULT_TERMS_EN,
    termsArabic: form.termsArabic || DEFAULT_TERMS_AR,
    vatPercentage,
    mail: Boolean(form.mail),
    attributes: {
      ...(extras.attributes || form.attributes || {}),
      approvedBy:
        extras.approvedBy ??
        extras.attributes?.approvedBy ??
        form.attributes?.approvedBy ??
        "",
      rejectedBy:
        extras.rejectedBy ??
        extras.attributes?.rejectedBy ??
        form.attributes?.rejectedBy ??
        "",
      approvedAt:
        extras.approvedAt ??
        extras.attributes?.approvedAt ??
        form.attributes?.approvedAt ??
        "",
      rejectedAt:
        extras.rejectedAt ??
        extras.attributes?.rejectedAt ??
        form.attributes?.rejectedAt ??
        "",
    },
  };
};

export const storePreview = (quote) => {
  sessionStorage.setItem(
    PREVIEW_KEY,
    JSON.stringify(
      normalizeQuote({
        ...quote,
        quoteNumber: quote.quoteNumber || "DRAFT",
        status: quote.status || "draft",
      }),
    ),
  );
};

export const createCancelToken = () => {
  const controller = new AbortController();
  return {
    token: controller.signal,
    cancel: () => controller.abort(),
  };
};

export const isRequestCanceled = (error) =>
  Boolean(
    error?.code === "ERR_CANCELED" ||
      error?.name === "CanceledError" ||
      error?.name === "AbortError" ||
      error?.message === "canceled",
  );

export const toMakeOption = (value) => {
  if (!value) return "";
  if (typeof value === "object") {
    const label = value.label || value.key || value.makeName || value.id;
    return {
      id: value.id || value.key || label,
      key: value.key || label,
      label,
      value: value.value || label,
    };
  }
  return { id: value, key: value, label: value, value: value };
};

export const toModelOption = (value) => {
  if (!value) return "";
  if (typeof value === "object") {
    const label = value.label || value.modelName || value.name || value.id;
    return {
      ...value,
      id: value.id,
      label,
      modelName: value.modelName || label,
    };
  }
  return { id: value, label: value, modelName: value };
};

export const applyAccountToForm = (prev, details, account, terms) => {
  const attrs = details.attributes || {};
  const contractNumber = details.contractNumber || account.contractNumber;
  const vatPercentage = resolveVatPercentage(details || account);
  const vatRate = vatPercentage / 100;
  const resetItems =
    prev.contractNumber && prev.contractNumber !== contractNumber;
  return {
    ...prev,
    account: {
      ...details,
      label: details.name || details.label,
      vatPercentage,
    },
    contactPerson:
      details.contact || details.accountContact || prev.contactPerson,
    mobile: details.phone || details.customerNumber || attrs.c_customerNo || "",
    email: details.email || attrs.c_email || "",
    address: attrs.c_address || details.address || "",
    vatNumber:
      attrs.c_taxNumber || details.vatNumber || details.taxNumber || "",
    vatPercentage,
    contractNumber,
    items: resetItems
      ? [emptyLineItem()]
      : (prev.items || []).map((row) => calcLine(row, vatRate)),
    ...terms,
  };
};

export const clearAccountFromForm = (prev) => ({
  ...prev,
  account: null,
  contactPerson: "",
  mobile: "",
  email: "",
  address: "",
  vatNumber: "",
  vatPercentage: DEFAULT_VAT_PERCENTAGE,
  contractNumber: "",
  items: [emptyLineItem()],
  ...defaultTerms(),
});

export const parseVinDecoderResponse = (responseData) => {
  if (!responseData || typeof responseData !== "object") return null;
  if (responseData.statusCode === "400") return null;
  if (responseData.data && typeof responseData.data === "object") {
    return responseData.data;
  }
  if (responseData.vin || responseData.maker) {
    return responseData;
  }
  return null;
};

export const normalizeVinFuelType = (fuelType) => {
  if (!fuelType) return "";
  const value = String(fuelType).trim().toLowerCase();
  if (value.includes("diesel")) return "DIESEL";
  if (value.includes("electric") || value === "bev") return "ELECTRIC";
  if (value.includes("plug") || value === "phev") return "PHEV";
  if (value.includes("hybrid")) return "HYBRID";
  if (value.includes("ethanol")) return "ETHANOL";
  if (value === "lpg") return "LPG";
  if (value === "cng") return "CNG";
  if (
    value.includes("gasoline") ||
    value.includes("petrol") ||
    value.includes("gas")
  ) {
    return "PETROL";
  }
  return String(fuelType).toUpperCase();
};

export const resolveVinCategory = (bodyType, categories = []) => {
  if (!bodyType) return "";
  const normalized = String(bodyType).trim();
  const alias = VIN_BODY_TYPE_ALIASES[normalized.toLowerCase()] || normalized;
  if (!Array.isArray(categories) || !categories.length) return alias;
  const exact = categories.find(
    (item) => String(item).toLowerCase() === alias.toLowerCase(),
  );
  if (exact) return exact;
  const partial = categories.find((item) => {
    const categoryValue = String(item).toLowerCase();
    const aliasValue = alias.toLowerCase();
    return (
      categoryValue.includes(aliasValue) || aliasValue.includes(categoryValue)
    );
  });
  return partial || alias;
};

export const formatMoney = (value) => Number(value || 0).toFixed(2);

export const formatDate = (value) =>
  value ? moment(value).format("DD MMM YYYY") : "-";

export const getQuoteIdFromUrl = () => {
  const queryString = window.location.search || "";
  const tokenPart =
    queryString.split("?&token=")[1] || queryString.split("token=")[1] || "";
  return (
    tokenPart.split("&timezone=")[0] ||
    tokenPart.split("&")[0] ||
    ""
  ).trim();
};

const formatVinDetailKey = (key) =>
  key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

export const getVinDetailLabel = (key, translate) => {
  const translated = translate(key);
  return translated && translated !== key ? translated : formatVinDetailKey(key);
};

export const formatVinDetailValue = (value) => {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

export const getVinDetailEntries = (data) => {
  if (!data) return [];
  const entries = [];
  Object.entries(data).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "") return;
    if (
      key === "settings" &&
      value &&
      typeof value === "object" &&
      !Array.isArray(value)
    ) {
      Object.entries(value).forEach(([settingKey, settingValue]) => {
        if (
          settingValue === null ||
          settingValue === undefined ||
          settingValue === ""
        ) {
          return;
        }
        entries.push([settingKey, settingValue]);
      });
      return;
    }
    if (typeof value === "object") return;
    entries.push([key, value]);
  });
  return entries;
};

export const resolveCityOption = (raw, cities = []) => {
  if (raw == null || raw === "") return null;
  const id = typeof raw === "object" ? raw.id ?? raw.cityId ?? raw.value : raw;
  const nameHint =
    typeof raw === "object"
      ? raw.cityEn || raw.name || raw.city || raw.label || ""
      : "";
  const found = (cities || []).find(
    (city) =>
      String(city.id) === String(id) ||
      (nameHint &&
        String(city.cityEn || city.name || "").toLowerCase() ===
          String(nameHint).toLowerCase()),
  );
  if (found) {
    return {
      ...found,
      id: found.id,
      value: found.id,
      label: found.cityEn || found.name || found.label || String(found.id),
    };
  }
  if (id == null || id === "") return null;
  return {
    ...(typeof raw === "object" ? raw : {}),
    id,
    value: id,
    label:
      (nameHint && String(nameHint) !== String(id) ? nameHint : "") ||
      String(id),
  };
};

export const mapVehicleToQuoteFields = (vehicle, cities = []) => {
  if (!vehicle) return {};
  const attrs = vehicle.attributes || {};
  const makeValue = attrs.make || vehicle.make || "";
  const modelLabel =
    vehicle.modelName ||
    vehicle.model?.modelName ||
    vehicle.model?.label ||
    attrs.model ||
    vehicle.model ||
    "";
  const modelId =
    vehicle.modelId && typeof vehicle.modelId === "object"
      ? {
          id: vehicle.modelId.id,
          label: vehicle.modelId.modelName || vehicle.modelId.label || modelLabel,
          modelName:
            vehicle.modelId.modelName || vehicle.modelId.label || modelLabel,
        }
      : vehicle.modelId
        ? {
            id: vehicle.modelId,
            label: modelLabel || String(vehicle.modelId),
            modelName: modelLabel || String(vehicle.modelId),
          }
        : modelLabel
          ? { id: modelLabel, label: modelLabel, modelName: modelLabel }
          : "";
  // City comes from vehicle attributes (API /vehicles/get), then top-level cityId
  const rawCity =
    attrs.cityId ||
    attrs.city ||
    attrs.cityEn ||
    (typeof vehicle.cityId === "object" ? vehicle.cityId : vehicle.cityId) ||
    vehicle.city ||
    null;
  const cityId = resolveCityOption(rawCity, cities);
  return {
    vehicleId: vehicle.id || null,
    vin: String(vehicle.vin || "").toUpperCase(),
    plate: vehicle.vehicleLicensePlate || vehicle.plate || "",
    make: toMakeOption(makeValue),
    model: modelLabel,
    modelId,
    year: attrs.year || vehicle.year || "",
    fuelType: attrs.fuel_type || vehicle.fuelType || "",
    vehicleType: vehicle.category || vehicle.vehicleType || "",
    label: vehicle.label || "",
    cityId,
    parentId: vehicle.parentId || null,
  };
};

export const getInitialForm = (user) => ({
  account: null,
  contactPerson: "",
  mobile: "",
  email: "",
  address: "",
  vatNumber: "",
  vatPercentage: DEFAULT_VAT_PERCENTAGE,
  quoteRefNo: "",
  expiryDate: moment().add(30, "days").toISOString(),
  vin: "",
  plate: "",
  make: "",
  model: "",
  modelId: "",
  year: "",
  fuelType: "",
  vehicleType: "",
  vehicleId: null,
  cityId: null,
  salesPerson: null,
  salesPersonEmail: "",
  salesPersonMobile: "",
  mail: false,
  items: [emptyLineItem()],
  ...defaultTerms(),
});

export const quoteToForm = (quote, user) => ({
  ...getInitialForm(user),
  account: quote.accountId || quote.accountName
    ? {
        id: quote.accountId || null,
        name: quote.accountName,
        label: quote.accountName,
        contractNumber: quote.contractNumber,
      }
    : null,
  accountName: quote.accountName || "",
  contactPerson: quote.customerName || quote.contactPerson || "",
  mobile: quote.contactPersonMobile || quote.mobile || "",
  email: quote.email || "",
  address: quote.address || "",
  vatNumber: quote.vatNumber || "",
  vatPercentage: resolveVatPercentage(
    quote.vatPercentage ?? quote.account ?? quote,
  ),
  quoteRefNo: quote.quoteRefNo || quote.quoteNumber || "",
  expiryDate: quote.expiryDate,
  vin: quote.vin || "",
  plate:
    quote.plate ||
    quote.attributes?.licensePlate ||
    "",
  make: toMakeOption(quote.make),
  model: quote.model || "",
  modelId: toModelOption(quote.modelId || quote.model),
  year: quote.year || "",
  fuelType: quote.fuelType || "",
  vehicleType: quote.vehicleType || "",
  vehicleId: quote.vehicleId || null,
  cityId: quote.cityId || null,
  salesPerson: quote.salesPersonId || quote.salePersonId
    ? {
        id: quote.salesPersonId || quote.salePersonId,
        value: quote.salesPersonId || quote.salePersonId,
        name:
          quote.salesPersonName ||
          quote.attributes?.salePersonName ||
          "",
        label:
          quote.salesPersonName ||
          quote.attributes?.salePersonName ||
          "",
        email:
          quote.salesPersonEmail ||
          quote.attributes?.salePersonEmail ||
          "",
        phone:
          quote.salesPersonMobile ||
          quote.attributes?.salePersonPhone ||
          quote.attributes?.salePersonMobile ||
          "",
      }
    : null,
  salesPersonEmail:
    quote.salesPersonEmail ||
    quote.attributes?.salePersonEmail ||
    "",
  salesPersonMobile:
    quote.salesPersonMobile ||
    quote.attributes?.salePersonPhone ||
    quote.attributes?.salePersonMobile ||
    "",
  mail: Boolean(
    quote.mail ??
      quote.attributes?.mail ??
      false,
  ),
  items:
    quote.items && quote.items.length
      ? quote.items.map((row) =>
          calcLine(
            row,
            resolveVatRate(quote.vatPercentage ?? quote.account ?? quote),
          ),
        )
      : [emptyLineItem()],
  termsEnglish: quote.termsEnglish,
  termsArabic: quote.termsArabic,
  contractNumber: quote.contractNumber,
});

/** Map an approved quotation into Manual Invoice create-form shape. */
export const quoteToManualInvoiceForm = (quote) => {
  const q = normalizeQuote(quote);
  const quantityPriceScale = (q.items || []).map((row) => {
    const quantity = Number(row.quantity) || 1;
    const price = Number(row.unitPrice) || 0;
    const vat = Number(row.vatAmount) || 0;
    const expenseType =
      row.expenseType && typeof row.expenseType === "object"
        ? row.expenseType
        : row.expenseTypeId
          ? {
              id: row.expenseTypeId,
              name: row.typeLabel || row.serviceType || row.expenseType || "",
              label: row.typeLabel || row.serviceType || row.expenseType || "",
            }
          : "";
    const subTypes =
      row.item && typeof row.item === "object"
        ? row.item
        : row.expenseSubTypeId
          ? {
              id: row.expenseSubTypeId,
              label: row.itemName || row.subTypeLabel || "",
              name: row.itemName || row.subTypeLabel || "",
              subTypeNum: row.itemCode || row.subTypeNum || "",
            }
          : "";
    return {
      expenseType,
      subTypes,
      expenseSubTypeId: row.expenseSubTypeId || subTypes?.id || null,
      quantity,
      price,
      description: row.description || "",
      itemName: row.itemName || row.subTypeLabel || "",
      itemCode: row.itemCode || row.subTypeNum || "",
      category: row.serviceType || row.typeLabel || "",
      purchaseCost: Number(row.cost) || 0,
      saleCost: price,
      vat,
      totalSingleItemCost:
        Number(row.lineTotal) || +(quantity * price + vat).toFixed(2),
    };
  });

  const allZeroVat =
    (q.items || []).length > 0 &&
    (q.items || []).every((row) => row.withVat === false);

  const descriptionParts = [
    q.quoteRefNo || q.quoteNumber
      ? `Quote: ${q.quoteRefNo || q.quoteNumber}`
      : "",
    q.plate ? `Plate: ${q.plate}` : "",
  ].filter(Boolean);

  // Quotation form keeps account on `account`; API/list may use accountId/accountName.
  const accountSrc =
    (quote?.account && typeof quote.account === "object"
      ? quote.account
      : null) ||
    (q.account && typeof q.account === "object" ? q.account : null) ||
    null;
  const accountId =
    accountSrc?.id ||
    accountSrc?.value ||
    q.accountId ||
    quote?.accountId ||
    null;
  const accountName =
    accountSrc?.name ||
    accountSrc?.label ||
    q.accountName ||
    quote?.accountName ||
    "";
  const contractNumber =
    accountSrc?.contractNumber ||
    q.contractNumber ||
    quote?.contractNumber ||
    "";

  return {
    refNum: q.quoteRefNo || q.quoteNumber || "",
    cn: accountId || accountName
      ? {
          id: accountId,
          value: accountId,
          label: accountName,
          name: accountName,
          contractNumber,
        }
      : null,
    refDate: q.quoteDate
      ? moment(q.quoteDate).endOf("day").utc().format()
      : moment().endOf("day").utc().format(),
    description: descriptionParts.join(" | "),
    zerovat: allZeroVat,
    quoteId: q.id || null,
    attributes: {
      quantityPriceScale: quantityPriceScale.length
        ? quantityPriceScale
        : [
            {
              expenseType: "",
              subTypes: "",
              expenseSubTypeId: null,
              quantity: "",
              price: "",
              description: "",
            },
          ],
    },
  };
};
